import React from 'react';

import { Box, Divider, Paper, SvgIconProps, Typography } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import { FlexSpacer } from '../../Layout';

export type BaseActivityItemProps = {
  icon: React.ElementType<SvgIconProps>;
  title: React.ReactNode;
  chip?: React.ReactNode;
  rightContent?: React.ReactNode;
  highlighted?: boolean;
  color?: 'primary' | 'secondary' | 'lightBlue' | 'warning';
  children?: React.ReactNode;
  onClick?: () => void;
  testId?: string;
};


const useStyles = makeStyles<Pick<BaseActivityItemProps, 'color'>>()((theme, props) => ({
  root: {
    minHeight: '83px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    border: 'none',
    boxShadow: `0px 0px 0px 1px ${theme.palette.divider} inset`
  },
  rootClickable: {
    cursor: 'pointer',
    transition: theme.transitions.create('box-shadow'),
    boxShadow: `0px 0px 0px 1px ${theme.palette.divider}`,
    '&:hover': {
      boxShadow: theme.shadows[3],
    }
  },
  rootHighlighted: {
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '4px',
      backgroundColor: props.color ? theme.palette[props.color].main : theme.palette.grey[400],
    }
  },
  title: {
    padding: theme.spacing('13px', 2),
    display: 'flex',
    alignItems: 'center',
  },
  titleIconWrapper: {
    color: props.color ? theme.palette[props.color].main : theme.palette.grey[400],
    backgroundColor: props.color ? theme.palette[props.color].background : theme.palette.grey[50],
    fontSize: 16,
    borderRadius: '50%',
    padding: 4,
    display: 'flex',
  },
  titleText: {
    marginLeft: theme.spacing(1),
    lineHeight: '24px',
    fontWeight: 500,
  },
  titleChip: {
    marginLeft: theme.spacing(1),
  },
  titleRest: {
  },
  content: {
    padding: theme.spacing(2),
  }
}));


export const BaseActivityItem: React.VFC<BaseActivityItemProps> = ({ color, highlighted, rightContent, icon: Icon, onClick, testId = 'base-activity-item', ...props }) => {
  const { classes, cx } = useStyles({ color });

  return (
    <Paper
      onClick={onClick}
      variant="outlined"
      className={cx(classes.root, {
        [classes.rootHighlighted]: highlighted,
        [classes.rootClickable]: !!onClick,
      })}
      data-testid={testId}
    >
      <Box className={classes.title}>
        <Box className={classes.titleIconWrapper}>
          <Icon fontSize="inherit" color={color ? 'inherit' : 'action'} />
        </Box>
        <Box className={classes.titleText}>
          <Typography>
            {props.title}
          </Typography>
        </Box>
        <Box className={classes.titleChip}>
          {props.chip}
        </Box>
        <FlexSpacer />
        {rightContent}
      </Box>
      {props.children && (
        <>
          <Divider />
          <Box className={classes.content}>
            {props.children}
          </Box>
        </>
      )}
    </Paper>
  );
};
